import {
  LabelPrinterDevice,
  LabelPrinterStrategy,
  LabelPrintOptions,
  PrintEntryLabelPayload,
} from './types'

const DEVICE: LabelPrinterDevice = {
  id: 'browser-default',
  name: 'Browser print dialog',
}

function buildLabelHtml(payload: PrintEntryLabelPayload, copies: number): string {
  const { entryCode, classCode, showName } = payload
  const safe = (value: string | null | undefined) =>
    (value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const label = `<div class="label">
      <div class="entry">${safe(entryCode)}</div>
      <div class="class">Class: ${safe(classCode ?? '')}</div>
      <div class="show">${safe(showName ?? '')}</div>
    </div>`

  // Each label must fit the 0.8in page body, or it spills onto a second page.
  // Fixed line heights leave room for a show name that wraps once.
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="referrer" content="no-referrer" />
    <title>Entry Label ${safe(entryCode)}</title>
    <style>
      @page { size: 2in 1in; margin: 0.1in; }
      body { margin: 0; font-family: 'Helvetica Neue', Arial, sans-serif; }
      .label {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        padding: 2px 8px;
        box-sizing: border-box;
        break-after: page;
        break-inside: avoid;
      }
      .label:last-child { break-after: auto; }
      .entry {
        font-size: 22px;
        line-height: 22px;
        font-weight: 700;
        letter-spacing: 1px;
      }
      .class {
        font-size: 12px;
        line-height: 14px;
        margin-top: 4px;
      }
      .show {
        font-size: 10px;
        line-height: 12px;
        margin-top: 2px;
        color: #555;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
      }
    </style>
  </head>
  <body>
    ${label.repeat(copies)}
  </body>
</html>`
}

function printLabel(payload: PrintEntryLabelPayload, copies: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const frame = document.createElement('iframe')
    frame.title = 'Exhibit label print'
    frame.setAttribute('aria-hidden', 'true')
    frame.tabIndex = -1
    Object.assign(frame.style, {
      position: 'fixed',
      left: '-10000px',
      top: '-10000px',
      width: '2in',
      height: '1in',
      border: '0',
    })
    const fail = (error: unknown) => {
      frame.remove()
      reject(error)
    }
    frame.addEventListener(
      'load',
      () => {
        try {
          const printWindow = frame.contentWindow
          if (!printWindow) throw new Error('Unable to open the browser print dialog.')
          // Some browsers return from print() while their dialog is still open.
          printWindow.addEventListener('afterprint', () => frame.remove(), { once: true })
          printWindow.print()
          resolve()
        } catch (error) {
          fail(error)
        }
      },
      { once: true }
    )
    try {
      frame.srcdoc = buildLabelHtml(payload, copies)
      document.body.append(frame)
    } catch (error) {
      fail(error)
    }
  })
}

export function createBrowserLabelPrinter(): LabelPrinterStrategy {
  return {
    kind: 'browser',
    label: 'Browser (PDF)',
    async listPrinters() {
      return [DEVICE]
    },
    async printEntryLabel(payload, options?: LabelPrintOptions) {
      const copies = Math.max(1, Math.floor(options?.copies ?? 1))
      await printLabel(payload, copies)
    },
  }
}
